import { ToggleGroup } from "radix-ui";
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

export interface SegmentedOption<V extends string> {
  value: V;
  label: ReactNode;
  disabled?: boolean;
  ariaLabel?: string;
}

/** Single-select segmented control (radix ToggleGroup: roving focus + arrow keys). */
export function Segmented<V extends string>({
  value,
  onChange,
  options,
  label,
  className,
  size = "md",
  full,
}: {
  value: V | undefined;
  onChange: (v: V) => void;
  options: SegmentedOption<V>[];
  label: string;
  className?: string;
  size?: "sm" | "md";
  full?: boolean;
}) {
  return (
    <ToggleGroup.Root
      type="single"
      value={value ?? ""}
      onValueChange={(v) => v && onChange(v as V)}
      aria-label={label}
      className={cn("inline-flex items-center gap-1 rounded-pill bg-slate-900/5 p-1", full && "flex w-full", className)}
    >
      {options.map((o) => (
        <ToggleGroup.Item
          key={o.value}
          value={o.value}
          disabled={o.disabled}
          aria-label={o.ariaLabel}
          className={cn(
            "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-pill font-semibold text-slate-600 transition-colors hover:text-slate-900 disabled:opacity-40 data-[state=on]:bg-white data-[state=on]:text-slate-900 data-[state=on]:shadow-[0_1px_3px_rgb(15_23_42/0.12)] [&_svg]:size-4",
            size === "sm" ? "h-8 px-3 text-[13px]" : "h-10 px-4 text-sm",
            full && "flex-1",
          )}
        >
          {o.label}
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  );
}
